import { useState } from "react";
import { useAuthContext } from "@mavenmm/teamwork-auth";
import { useGraphQL } from "../hooks/useGraphQL";

// Exact queries from maven-dashboard AdminPage
const PRESET_QUERIES = [
  {
    name: "🫛 Pods (full)",
    query: `query {
  pods {
    id
    name
    senior_brand_maven_lead
    is_deleted
    sub_pods {
      id
      name
      is_deleted
      pod_id
      members {
        id
        first_name
        last_name
        email_address
        avatar_url
        title
      }
    }
    members {
      id
      first_name
      last_name
      email_address
    }
  }
}`,
  },
  {
    name: "👥 AdminGetAllTeams (exact)",
    query: `query AdminGetAllTeams {
  teams {
    id
    teamName
    order
  }
}`,
  },
  {
    name: "🔍 Who am I?",
    query: `query {
  me {
    id
    first_name
    last_name
    email_address
  }
}`,
  },
  {
    name: "🔬 Introspect SubPod",
    query: `query {
  __type(name: "SubPod") {
    name
    fields {
      name
      type { name kind }
    }
  }
}`,
  },
];

export default function GraphQLPlayground() {
  const { isAuthenticated } = useAuthContext();
  const { data, errors, isLoading, execute, rawResponse } = useGraphQL<Record<string, unknown>>();
  const [query, setQuery] = useState(PRESET_QUERIES[0].query);

  const handleExecute = () => {
    if (query.trim()) {
      execute(query);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">Please log in to use the GraphQL playground 🔐</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">
          GraphQL Playground 🎮
        </h1>
        <p className="text-gray-500 text-sm">
          Query the dashboard API like you own the place (read-only, we're not animals)
        </p>
      </div>

      {/* Preset buttons */}
      <div className="mb-4 flex flex-wrap gap-2">
        {PRESET_QUERIES.map((preset) => (
          <button
            key={preset.name}
            onClick={() => setQuery(preset.query)}
            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
          >
            {preset.name}
          </button>
        ))}
      </div>

      {/* Query input */}
      <div className="mb-4">
        <textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full h-48 p-4 font-mono text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
          placeholder="Enter your GraphQL query here..."
          spellCheck={false}
        />
      </div>

      {/* Execute button */}
      <div className="mb-6">
        <button
          onClick={handleExecute}
          disabled={isLoading || !query.trim()}
          className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? "Running... 🏃" : "Execute Query 🚀"}
        </button>
      </div>

      {/* Results */}
      <div className="space-y-4">
        {/* Errors */}
        {errors && errors.length > 0 && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <h3 className="font-semibold text-red-800 mb-2">Oops! 💥</h3>
            <ul className="list-disc list-inside text-red-700 text-sm">
              {errors.map((error, i) => (
                <li key={i}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Success data */}
        {data && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <h3 className="font-semibold text-green-800 mb-2">
              Success! Here's your data 🎉
            </h3>
            <pre className="overflow-auto text-sm bg-white dark:bg-maven-gray-800 dark:text-maven-gray-100 p-4 rounded border border-green-100 dark:border-maven-gray-800 max-h-96">
              {JSON.stringify(data, null, 2)}
            </pre>
          </div>
        )}

        {/* Raw response for debugging */}
        {rawResponse && (
          <details className="text-sm">
            <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
              🔬 Show raw response (for nerds)
            </summary>
            <pre className="mt-2 overflow-auto text-xs bg-gray-50 p-4 rounded border max-h-64">
              {JSON.stringify(rawResponse, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
